import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import EnhancedModuleEditor from './EnhancedModuleEditor';

// Team Management Dashboard Component  
const TeamDashboard = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamAnalytics, setTeamAnalytics] = useState(null);
  const [teamModules, setTeamModules] = useState([]);
  const [teamStatus, setTeamStatus] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberProgress, setMemberProgress] = useState(null);
  const [showCreateModule, setShowCreateModule] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Exam candidates states
  const [examCandidates, setExamCandidates] = useState({ ready_for_exam: [], exam_booked: [], exam_failed: [] });
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedRetryCategories, setSelectedRetryCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teamTab, setTeamTab] = useState('overview');
  const [teamExamOverview, setTeamExamOverview] = useState([]);
  const [expandedVLTeams, setExpandedVLTeams] = useState({});
  const [selectedExamMember, setSelectedExamMember] = useState(null);
  
  // TL: Mitarbeiter search/filter/sort
  const [memberSearch, setMemberSearch] = useState('');
  const [memberStatusFilter, setMemberStatusFilter] = useState('all');
  const [memberProgressFilter, setMemberProgressFilter] = useState('all');
  const [memberSort, setMemberSort] = useState('last_activity');

  useEffect(() => {
    if (user?.role === 'teamleiter' || user?.role === 'vertriebsleiter') {
      fetchTeamData();
    }
  }, [user]);

  const fetchTeamData = async () => {
    try {
      // ── Stufe 1: Kritische Daten → UI sofort zeigen ───────────────────────
      const [membersRes, teamStatusRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/teamleiter/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/teamleiter/team-status`).catch(() => ({ data: {} })),
        axios.get(`${API}/categories`).catch(() => ({ data: [] })),
      ]);

      setTeamMembers(membersRes.data);
      setTeamStatus(teamStatusRes.data);
      setCategories(categoriesRes.data);
      setLoading(false); // UI sofort sichtbar

      // ── Stufe 2: Sekundäre Daten parallel nachladen ────────────────────────
      const secondaryRequests = [
        axios.get(`${API}/teamleiter/analytics`).catch(() => ({ data: null })),
        axios.get(`${API}/vertriebsleiter/exam-candidates`).catch(() => ({ data: { ready_for_exam: [], exam_booked: [], exam_failed: [] } })),
      ];
      if (user?.role === 'vertriebsleiter') {
        secondaryRequests.push(axios.get(`${API}/teamleiter/modules`).catch(() => ({ data: [] })));
      }
      if (user?.role === 'teamleiter') {
        secondaryRequests.push(axios.get(`${API}/teamleiter/team-exam-overview`).catch(() => ({ data: { members: [] } })));
      }

      const secondary = await Promise.all(secondaryRequests);
      setTeamAnalytics(secondary[0].data);
      setExamCandidates(secondary[1]?.data || { ready_for_exam: [], exam_booked: [], exam_failed: [] });
      if (user?.role === 'vertriebsleiter') setTeamModules(secondary[2]?.data || []);
      if (user?.role === 'teamleiter') setTeamExamOverview(secondary[2]?.data?.members || []);
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberProgress = async (memberId) => {
    try {
      const response = await axios.get(`${API}/teamleiter/progress/${memberId}`);
      setMemberProgress(response.data);
    } catch (error) {
      console.error('Error fetching member progress:', error);
    }
  };

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    fetchMemberProgress(member.id || member.user_id);
  };

  const handleModuleSuccess = () => {
    setShowCreateModule(false);
    fetchTeamData(); // Refresh data after creating module
  };
  
  // Handle exam result submission
  const handleExamResult = async (userId, passed) => {
    try {
      const response = await axios.post(`${API}/vertriebsleiter/exam-result`, {
        user_id: userId,
        passed: passed,
        retry_category_ids: passed ? [] : selectedRetryCategories
      });
      
      toast.success(passed ? 'Prüfung bestanden! Zertifikat wurde versendet.' : 'Prüfung nicht bestanden. Kategorien wurden zur Wiederholung freigegeben.');
      setSelectedCandidate(null);
      setSelectedRetryCategories([]);
      fetchTeamData(); // Refresh
    } catch (error) {
      console.error('Error submitting exam result:', error);
      toast.error('Fehler beim Speichern des Ergebnisses');
    }
  };
  
  const toggleCategoryForRetry = (categoryId) => {
    setSelectedRetryCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const isVertriebsleiter = user.role === 'vertriebsleiter';
  const canCreateModules = user.role === 'vertriebsleiter'; // Only vertriebsleiter can create modules

  return (
    <div className="bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Compact Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {isVertriebsleiter ? 'Vertriebsleitung' : 'Team Dashboard'}
          </h1>
          <p className="text-sm text-gray-500">
            {isVertriebsleiter 
              ? `${teamAnalytics?.total_teams || 0} Teams · ${teamAnalytics?.total_members_all_teams || 0} Mitarbeiter`
              : `${teamMembers.length} Vertriebler`}
          </p>
        </div>

        {/* Sub-Tab Navigation */}
        <div className="flex space-x-1 bg-white rounded-lg p-1 shadow-sm mb-6 border border-gray-200">
          {[
            { id: 'overview', label: 'Übersicht' },
            { id: 'members', label: 'Mitarbeiter' },
            { id: 'exams', label: 'Abschlussgespräche' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setTeamTab(tab.id)}
              data-testid={`team-tab-${tab.id}`}
              className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                teamTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              {tab.id === 'exams' && (examCandidates.ready_for_exam?.length + examCandidates.exam_booked?.length) > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {examCandidates.ready_for_exam.length + examCandidates.exam_booked.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ===== TAB: Übersicht ===== */}
        {teamTab === 'overview' && (
          <>
            {/* Stats Cards - nur für Vertriebsleiter */}
            {teamAnalytics && isVertriebsleiter && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="stat-card bg-white rounded-xl shadow-sm p-4 border-l-4 border-l-blue-500">
                  <div className="text-2xl font-bold text-blue-600">
                    {isVertriebsleiter ? teamAnalytics.total_teams : teamMembers.length}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{isVertriebsleiter ? 'Teams' : 'Mitglieder'}</div>
                </div>
                <div className="stat-card bg-white rounded-xl shadow-sm p-4 border-l-4 border-l-emerald-500">
                  <div className="text-2xl font-bold text-emerald-600">
                    {isVertriebsleiter ? teamAnalytics.total_members_all_teams : teamAnalytics.total_team_members}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{isVertriebsleiter ? 'Gesamt' : 'Mitglieder'}</div>
                </div>
                <div className="stat-card bg-white rounded-xl shadow-sm p-4 border-l-4 border-l-slate-400">
                  <div className="text-2xl font-bold text-slate-700">
                    {isVertriebsleiter ? teamAnalytics.total_completed_all_teams : teamAnalytics.completed_modules_total}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Module abgeschlossen</div>
                </div>
                <div className="stat-card bg-white rounded-xl shadow-sm p-4 border-l-4 border-l-amber-500">
                  <div className="text-2xl font-bold text-amber-600">
                    {typeof teamAnalytics.team_completion_rate === 'string' 
                      ? parseFloat(teamAnalytics.team_completion_rate).toFixed(1) || 0
                      : teamAnalytics.team_completion_rate?.toFixed(1) || 0}%
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Fortschritt</div>
                </div>
              </div>
            )}

            {/* ── TEAMLEITER: Leadership Übersicht ── */}
            {!isVertriebsleiter && teamStatus && (() => {
              const members = teamStatus.team_members || [];
              const now = new Date();
              const daysSince = (dateStr) => dateStr ? Math.floor((now - new Date(dateStr)) / 86400000) : 999;
              const inactiveMembers = members.filter(m => m.account_status === 'active' && daysSince(m.last_activity) > 14);
              const noProgressMembers = members.filter(m => m.account_status === 'active' && m.average_progress === 0);
              const readyForExam = (teamExamOverview || []).filter(m => m.exam_status === 'ready_for_exam');
              const pendingActivation = members.filter(m => m.account_status === 'pending');
              const attentionCount = inactiveMembers.length + noProgressMembers.length + readyForExam.length + pendingActivation.length;
              
              return (
                <>
                {/* ── Priority / Attention Box ── */}
                {attentionCount > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      <h3 className="text-sm font-bold text-amber-800">Aufmerksamkeit erforderlich ({attentionCount})</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {readyForExam.length > 0 && (
                        <div className="bg-white border border-indigo-200 rounded-lg px-3 py-2.5 flex items-center gap-2.5 cursor-pointer hover:bg-indigo-50 transition-colors" onClick={() => setTeamTab('exams')} data-testid="attention-ready-exam">
                          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-indigo-700 leading-none">{readyForExam.length}</p>
                            <p className="text-xs text-indigo-500 mt-0.5">Bereit f. Gespräch</p>
                          </div>
                        </div>
                      )}
                      {inactiveMembers.length > 0 && (
                        <div className="bg-white border border-red-200 rounded-lg px-3 py-2.5 flex items-center gap-2.5 cursor-pointer hover:bg-red-50 transition-colors" onClick={() => { setTeamTab('members'); setMemberStatusFilter('inactive'); }} data-testid="attention-inactive">
                          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-red-700 leading-none">{inactiveMembers.length}</p>
                            <p className="text-xs text-red-500 mt-0.5">Inaktiv (&gt;14 Tage)</p>
                          </div>
                        </div>
                      )}
                      {noProgressMembers.length > 0 && (
                        <div className="bg-white border border-orange-200 rounded-lg px-3 py-2.5 flex items-center gap-2.5 cursor-pointer hover:bg-orange-50 transition-colors" onClick={() => { setTeamTab('members'); setMemberProgressFilter('none'); }} data-testid="attention-no-progress">
                          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/></svg>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-orange-700 leading-none">{noProgressMembers.length}</p>
                            <p className="text-xs text-orange-500 mt-0.5">Kein Fortschritt</p>
                          </div>
                        </div>
                      )}
                      {pendingActivation.length > 0 && (
                        <div className="bg-white border border-yellow-200 rounded-lg px-3 py-2.5 flex items-center gap-2.5" data-testid="attention-pending">
                          <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-yellow-700 leading-none">{pendingActivation.length}</p>
                            <p className="text-xs text-yellow-600 mt-0.5">Aussteh. Aktivierung</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── KPI Row ── */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                  {[
                    { label: 'Aktive Mitglieder', value: members.filter(m => m.account_status === 'active').length, total: members.length, color: 'green', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/> },
                    { label: 'Inaktiv (>14 Tage)', value: inactiveMembers.length, total: members.length, color: inactiveMembers.length > 0 ? 'red' : 'gray', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/> },
                    { label: 'Mit Lernfortschritt', value: members.filter(m => m.average_progress > 0).length, total: members.length, color: 'blue', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/> },
                    { label: 'Bereit f. Gespräch', value: readyForExam.length, total: members.length, color: readyForExam.length > 0 ? 'indigo' : 'gray', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"/> },
                  ].map(({ label, value, total, color, icon }) => (
                    <div key={label} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-tight">{label}</p>
                        </div>
                        <div className={`w-8 h-8 bg-${color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <svg className={`w-4 h-4 text-${color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">{icon}</svg>
                        </div>
                      </div>
                      <div className="mt-2.5 w-full bg-gray-100 rounded-full h-1">
                        <div className={`h-1 rounded-full bg-${color}-500 transition-all`} style={{ width: `${total > 0 ? (value/total)*100 : 0}%` }} />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{total > 0 ? Math.round((value/total)*100) : 0}% des Teams</p>
                    </div>
                  ))}
                </div>

                {/* ── Team Fortschritt + Aktivitätsliste ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  {/* Durchschnittlicher Fortschritt */}
                  <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Durchschnittlicher Teamfortschritt</h3>
                    {(() => {
                      const activeM = members.filter(m => m.account_status === 'active');
                      const avg = activeM.length > 0 ? activeM.reduce((s, m) => s + (m.average_progress || 0), 0) / activeM.length : 0;
                      return (
                        <>
                          <div className="flex items-end gap-2 mb-3">
                            <span className="text-3xl font-bold text-gray-900">{avg.toFixed(0)}%</span>
                            <span className="text-sm text-gray-400 pb-1">Ø Fortschritt</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-3 mb-4">
                            <div className="h-3 rounded-full transition-all duration-700"
                              style={{ width: `${avg}%`, background: avg >= 70 ? '#22c55e' : avg >= 30 ? '#f59e0b' : '#ef4444' }} />
                          </div>
                          <div className="space-y-2.5">
                            {members.filter(m => m.account_status === 'active').sort((a,b) => (b.average_progress||0)-(a.average_progress||0)).slice(0,5).map(m => (
                              <div key={m.id} className="flex items-center gap-2">
                                <div className="w-24 text-xs text-gray-600 truncate">{m.name.split(' ')[0]}</div>
                                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                  <div className="h-1.5 rounded-full" style={{ width: `${m.average_progress||0}%`, background: (m.average_progress||0) >= 50 ? '#22c55e' : (m.average_progress||0) > 0 ? '#f59e0b' : '#e5e7eb' }} />
                                </div>
                                <span className="text-xs text-gray-500 w-8 text-right">{m.average_progress||0}%</span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Letzte Aktivität */}
                  <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Letzte Aktivitäten</h3>
                    <div className="space-y-2.5">
                      {[...members].sort((a,b) => new Date(b.last_activity||0) - new Date(a.last_activity||0)).slice(0,6).map(m => {
                        const d = daysSince(m.last_activity);
                        const color = d <= 2 ? 'green' : d <= 7 ? 'yellow' : d <= 14 ? 'orange' : 'red';
                        const label = d === 0 ? 'Heute' : d === 1 ? 'Gestern' : d < 7 ? `${d} Tage` : d < 30 ? `${Math.floor(d/7)}W` : `${Math.floor(d/30)}M`;
                        return (
                          <div key={m.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 bg-${color}-500`} />
                              <span className="text-sm text-gray-700 truncate">{m.name}</span>
                            </div>
                            <span className={`text-xs font-medium text-${color}-600 flex-shrink-0 ml-2`}>{label}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
                      {[
                        { label: 'Heute', count: members.filter(m => daysSince(m.last_activity) <= 1).length, color: 'green' },
                        { label: '7 Tage', count: members.filter(m => daysSince(m.last_activity) <= 7).length, color: 'yellow' },
                        { label: '>14 Tage', count: members.filter(m => daysSince(m.last_activity) > 14).length, color: 'red' },
                      ].map(({ label, count, color }) => (
                        <div key={label}>
                          <p className={`text-xl font-bold text-${color}-600`}>{count}</p>
                          <p className="text-xs text-gray-400">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                </>
              );
            })()}

        {/* Vertriebsleiter: Team Overview */}
        {isVertriebsleiter && teamAnalytics?.team_analytics && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Team-Übersicht</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {teamAnalytics.team_analytics.map((team) => (
                <div key={team.team_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{team.team_leader}</h3>
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {team.team_members_count} Mitglieder
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-3">{team.team_leader_email}</div>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Fortschritt</span>
                      <span>
                        {typeof team.completion_rate === 'string' 
                          ? parseFloat(team.completion_rate).toFixed(1)
                          : team.completion_rate.toFixed(1)
                        }%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                        style={{ width: `${Math.min(team.completion_rate, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{team.completed_modules} / {team.expected_completions} Module</span>
                    <span className={`font-medium ${
                      team.completion_rate >= 80 ? 'text-green-600' :
                      team.completion_rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {team.completion_rate >= 80 ? 'Exzellent' :
                       team.completion_rate >= 50 ? 'Gut' : 'Verbesserung nötig'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* End of Übersicht tab content */}
          </>
        )}

        {/* ===== TAB: Mitarbeiter ===== */}
        {teamTab === 'members' && (
          <>
        {/* Team Members - Accordion grouped by Teamleiter (VL) or flat list (TL) */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {isVertriebsleiter ? 'Teams verwalten' : 'Mein Team'}
          </h2>
          
          {teamMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Noch keine Teammitglieder zugewiesen.</p>
            </div>
          ) : isVertriebsleiter ? (
            /* ── VL Accordion View - grouped by Teamleiter ── */
            (() => {
              const grouped = {};
              const noManager = [];
              const members = teamMembers;
              
              // First, find all Teamleiter users and create empty groups for them
              members.forEach(m => {
                if (m.role === 'teamleiter') {
                  const tlName = m.name;
                  if (!grouped[tlName]) grouped[tlName] = { members: [], teamleiter: m };
                }
              });
              
              // Then group Vertriebler by their manager
              members.forEach(m => {
                if (m.role === 'vertriebler') {
                  const key = m.manager_name || m.teamleiter_name;
                  if (key) {
                    if (!grouped[key]) grouped[key] = { members: [], teamleiter: null };
                    grouped[key].members.push(m);
                  } else {
                    noManager.push(m);
                  }
                }
              });
              
              const allKeys = Object.keys(grouped);
              const allExpanded = allKeys.length > 0 && allKeys.every(k => expandedVLTeams[k]);

              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-400">Klicke auf einen Teamleiter um die Vertriebler aufzuklappen.</p>
                    <button onClick={() => {
                      const newState = allExpanded ? {} : allKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {});
                      setExpandedVLTeams(newState);
                    }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                      {allExpanded ? 'Alle zuklappen' : 'Alle aufklappen'}
                    </button>
                  </div>

                  {allKeys.sort().map(tlName => (
                    <div key={tlName} className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedVLTeams(prev => ({ ...prev, [tlName]: !prev[tlName] }))}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                        data-testid={`vl-team-toggle-${tlName}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-blue-700">{tlName.charAt(0)}</span>
                          </div>
                          <div className="text-left">
                            <span className="text-sm font-bold text-gray-900">{tlName}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">Teamleiter</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{grouped[tlName].members.length} Vertriebler</span>
                          <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedVLTeams[tlName] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      {expandedVLTeams[tlName] && (
                        <div className="border-t border-gray-100 px-5 py-3 space-y-2">
                          {grouped[tlName].members.length === 0 ? (
                            <div className="text-center py-4 text-gray-400 text-sm">Keine Vertriebler zugewiesen</div>
                          ) : grouped[tlName].members.map(member => (
                            <div key={member.id || member.user_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 gap-3"
                              data-testid={`vl-member-${member.id || member.user_id}`}>
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-800 block">{member.name}</span>
                                <span className="text-xs text-gray-400 block truncate">{member.email}</span>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                {/* Progress bar */}
                                <div className="flex items-center gap-2">
                                  <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                    <div className="h-1.5 rounded-full bg-gradient-to-r from-green-500 to-green-600"
                                      style={{ width: `${Math.min(member.completion_rate || 0, 100)}%` }} />
                                  </div>
                                  <span className="text-xs text-gray-500 min-w-[32px]">
                                    {(member.completion_rate || 0).toFixed(0)}%
                                  </span>
                                </div>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  (member.is_active !== false) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {(member.is_active !== false) ? 'Aktiv' : 'Inaktiv'}
                                </span>
                                <button onClick={() => handleMemberSelect(member)}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2.5 py-1 rounded-full"
                                  data-testid={`vl-detail-btn-${member.id || member.user_id}`}>
                                  Details
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {noManager.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                      <p className="text-sm font-semibold text-orange-800 mb-3">Ohne Teamleiter ({noManager.length})</p>
                      <div className="space-y-2">
                        {noManager.map(member => (
                          <div key={member.id || member.user_id} className="flex items-center justify-between bg-white border border-orange-200 rounded-lg px-4 py-3 gap-3">
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-orange-800 block">{member.name}</span>
                              <span className="text-xs text-gray-400 block truncate">{member.email}</span>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                  <div className="h-1.5 rounded-full bg-gradient-to-r from-green-500 to-green-600"
                                    style={{ width: `${Math.min(member.completion_rate || 0, 100)}%` }} />
                                </div>
                                <span className="text-xs text-gray-500">{(member.completion_rate || 0).toFixed(0)}%</span>
                              </div>
                              <button onClick={() => handleMemberSelect(member)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2.5 py-1 rounded-full">
                                Details
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            /* ── TL: Filtered + Sortable Member List ── */
            (() => {
              const now = new Date();
              const daysSince = (dateStr) => dateStr ? Math.floor((now - new Date(dateStr)) / 86400000) : 999;
              const rawMembers = teamMembers.filter(m => m.role !== 'teamleiter');
              
              // Filter + Search
              let filtered = rawMembers.filter(m => {
                const nameMatch = m.name.toLowerCase().includes(memberSearch.toLowerCase()) || m.email.toLowerCase().includes(memberSearch.toLowerCase());
                const statusMatch = memberStatusFilter === 'all' ? true
                  : memberStatusFilter === 'active' ? (m.is_active !== false && m.account_status === 'active')
                  : memberStatusFilter === 'inactive' ? (daysSince(m.last_active || m.last_activity) > 14)
                  : memberStatusFilter === 'pending' ? (m.account_status === 'pending')
                  : true;
                const progressMatch = memberProgressFilter === 'all' ? true
                  : memberProgressFilter === 'with' ? ((m.completion_rate || 0) > 0)
                  : memberProgressFilter === 'none' ? ((m.completion_rate || 0) === 0)
                  : true;
                return nameMatch && statusMatch && progressMatch;
              });
              
              // Sort
              filtered = [...filtered].sort((a, b) => {
                if (memberSort === 'last_activity') return new Date(b.last_active || b.last_activity || 0) - new Date(a.last_active || a.last_activity || 0);
                if (memberSort === 'progress_desc') return (b.completion_rate || 0) - (a.completion_rate || 0);
                if (memberSort === 'progress_asc') return (a.completion_rate || 0) - (b.completion_rate || 0);
                if (memberSort === 'name') return a.name.localeCompare(b.name, 'de');
                return 0;
              });

              return (
                <>
                  {/* Controls */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="relative flex-1 min-w-48">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                      <input
                        type="text"
                        placeholder="Name oder E-Mail suchen..."
                        value={memberSearch}
                        onChange={e => setMemberSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="member-search"
                      />
                    </div>
                    <select value={memberStatusFilter} onChange={e => setMemberStatusFilter(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="member-status-filter">
                      <option value="all">Alle Status</option>
                      <option value="active">Aktiv</option>
                      <option value="inactive">Inaktiv (&gt;14 Tage)</option>
                      <option value="pending">Ausstehend</option>
                    </select>
                    <select value={memberProgressFilter} onChange={e => setMemberProgressFilter(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="member-progress-filter">
                      <option value="all">Alle Fortschritte</option>
                      <option value="with">Mit Fortschritt</option>
                      <option value="none">Kein Fortschritt</option>
                    </select>
                    <select value={memberSort} onChange={e => setMemberSort(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="member-sort">
                      <option value="last_activity">Letzte Aktivität</option>
                      <option value="progress_desc">Fortschritt ↓</option>
                      <option value="progress_asc">Fortschritt ↑</option>
                      <option value="name">Name A–Z</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">{filtered.length} von {rawMembers.length} Mitarbeiter{rawMembers.length !== 1 ? 'n' : ''}</p>

                  {/* Member Cards */}
                  <div className="space-y-2">
                    {filtered.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">Keine Mitarbeiter gefunden.</div>
                    ) : filtered.map(member => {
                      const d = daysSince(member.last_active || member.last_activity);
                      const isInactive = d > 14;
                      const noProgress = (member.completion_rate || 0) === 0;
                      const isPending = member.account_status === 'pending';
                      const progress = member.completion_rate || 0;
                      const progressColor = progress >= 70 ? '#22c55e' : progress >= 30 ? '#f59e0b' : '#ef4444';
                      const activityLabel = d === 0 ? 'Heute' : d === 1 ? 'Gestern' : d < 7 ? `${d} Tage` : d < 30 ? `${Math.floor(d/7)} Woche(n)` : `${Math.floor(d/30)} Monat(e)`;

                      return (
                        <div key={member.id || member.user_id}
                          className={`flex items-center gap-4 rounded-xl px-4 py-3.5 border transition-all hover:shadow-sm ${
                            isInactive ? 'border-l-4 border-l-red-400 border-gray-100 bg-red-50/30' :
                            isPending ? 'border-l-4 border-l-yellow-400 border-gray-100 bg-yellow-50/20' :
                            noProgress ? 'border-l-4 border-l-orange-400 border-gray-100 bg-orange-50/20' :
                            'border-gray-100 bg-white'
                          }`}
                          data-testid={`tl-member-${member.id || member.user_id}`}>
                          
                          {/* Avatar */}
                          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
                            style={{ background: isInactive ? '#f87171' : isPending ? '#fbbf24' : '#3b82f6' }}>
                            {member.name.charAt(0)}
                          </div>

                          {/* Name + Email */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-gray-800">{member.name}</span>
                              {member.role === 'teamleiter' && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">Teamleiter</span>}
                              {isInactive && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">Inaktiv</span>}
                              {isPending && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">Ausstehend</span>}
                              {!isInactive && !isPending && noProgress && member.role !== 'teamleiter' && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">Kein Fortschritt</span>}
                            </div>
                            <span className="text-xs text-gray-400">{member.email}</span>
                          </div>

                          {/* Progress */}
                          <div className="hidden sm:flex items-center gap-2 w-32 flex-shrink-0">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full transition-all" style={{ width: `${Math.min(progress,100)}%`, backgroundColor: progressColor }} />
                            </div>
                            <span className="text-xs font-medium text-gray-600 w-7 text-right">{progress.toFixed(0)}%</span>
                          </div>

                          {/* Last Activity */}
                          <div className="hidden md:block flex-shrink-0 w-24 text-right">
                            <span className={`text-xs font-medium ${isInactive ? 'text-red-500' : d <= 2 ? 'text-green-600' : 'text-gray-500'}`}>
                              {activityLabel}
                            </span>
                          </div>

                          {/* Details Button */}
                          <button onClick={() => handleMemberSelect(member)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2.5 py-1.5 rounded-lg flex-shrink-0 transition-colors"
                            data-testid={`tl-detail-btn-${member.id || member.user_id}`}>
                            Details
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()
          )}
        </div>

        {/* Member Progress Detail Modal */}
        {selectedMember && memberProgress && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-t-2xl p-5">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {memberProgress.member.name.charAt(0)}
                    </div>
                    <div>
                      <h2 className="text-white font-bold text-lg leading-tight">{memberProgress.member.name}</h2>
                      <p className="text-slate-300 text-xs">{memberProgress.member.email}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedMember(null)} className="text-white/70 hover:text-white p-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* Status Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    {
                      label: 'Fortschritt',
                      value: `${typeof memberProgress.progress_summary.completion_rate === 'string'
                        ? parseFloat(memberProgress.progress_summary.completion_rate).toFixed(0)
                        : memberProgress.progress_summary.completion_rate.toFixed(0)}%`,
                      sub: `${memberProgress.progress_summary.completed_modules}/${memberProgress.progress_summary.total_modules} Module`,
                      color: 'blue'
                    },
                    {
                      label: 'Account Status',
                      value: memberProgress.member.account_status === 'active' ? 'Aktiv' : memberProgress.member.account_status === 'pending' ? 'Ausstehend' : 'Inaktiv',
                      color: memberProgress.member.account_status === 'active' ? 'green' : memberProgress.member.account_status === 'pending' ? 'yellow' : 'red',
                    },
                    {
                      label: 'Akademie Status',
                      value: {
                        exam_passed: 'Bestanden',
                        exam_booked: 'Termin gebucht',
                        ready_for_exam: 'Bereit',
                        exam_failed: 'Nicht bestanden',
                      }[memberProgress.member.exam_status] || 'In Ausbildung',
                      color: memberProgress.member.exam_status === 'exam_passed' ? 'green' : memberProgress.member.exam_status === 'exam_booked' ? 'blue' : memberProgress.member.exam_status === 'ready_for_exam' ? 'indigo' : 'gray',
                    },
                    {
                      label: 'Letzte Aktivität',
                      value: memberProgress.member.last_activity
                        ? (() => { const d = Math.floor((new Date() - new Date(memberProgress.member.last_activity)) / 86400000); return d === 0 ? 'Heute' : d === 1 ? 'Gestern' : `${d} Tage`; })()
                        : 'Nie',
                      color: (() => { const d = memberProgress.member.last_activity ? Math.floor((new Date() - new Date(memberProgress.member.last_activity)) / 86400000) : 999; return d <= 2 ? 'green' : d <= 7 ? 'yellow' : 'red'; })(),
                    },
                  ].map(({ label, value, sub, color }) => (
                    <div key={label} className={`bg-${color}-50 border border-${color}-100 rounded-xl p-3`}>
                      <p className={`text-lg font-bold text-${color}-700 leading-tight`}>{value}</p>
                      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
                      <p className="text-xs text-gray-500 mt-1">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Progress Bar */}
                {memberProgress.progress_summary.total_modules > 0 && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Gesamtfortschritt</span>
                      <span>{memberProgress.progress_summary.completed_modules} von {memberProgress.progress_summary.total_modules} Modulen abgeschlossen</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div className="h-2.5 rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.min(parseFloat(memberProgress.progress_summary.completion_rate), 100)}%`,
                          background: parseFloat(memberProgress.progress_summary.completion_rate) >= 70 ? '#22c55e' : parseFloat(memberProgress.progress_summary.completion_rate) >= 30 ? '#f59e0b' : '#ef4444'
                        }} />
                    </div>
                  </div>
                )}

                {/* Module Details - grouped by category */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Module</h3>
                  {memberProgress.detailed_progress.length === 0 ? (
                    <p className="text-sm text-gray-400">Noch keine Module zugewiesen.</p>
                  ) : (() => {
                    // Group modules by category
                    const grouped = {};
                    const groupOrder = {};
                    memberProgress.detailed_progress.forEach(mod => {
                      const key = mod.category_id || '__none__';
                      if (!grouped[key]) {
                        grouped[key] = { title: mod.category_title || 'Ohne Kategorie', order: mod.category_order ?? 9999, modules: [] };
                        groupOrder[key] = mod.category_order ?? 9999;
                      }
                      grouped[key].modules.push(mod);
                    });
                    const sortedKeys = Object.keys(grouped).sort((a, b) => groupOrder[a] - groupOrder[b]);
                    return (
                      <div className="space-y-4">
                        {sortedKeys.map(key => {
                          const group = grouped[key];
                          const completedCount = group.modules.filter(m => m.completed).length;
                          const totalCount = group.modules.length;
                          const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
                          const barColor = pct === 100 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#94a3b8';
                          return (
                            <div key={key}>
                              {/* Category Header */}
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{group.title}</span>
                                <span className="text-xs font-medium" style={{ color: barColor }}>{completedCount}/{totalCount}</span>
                              </div>
                              {/* Progress bar */}
                              <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2">
                                <div className="h-1.5 rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%`, backgroundColor: barColor }} />
                              </div>
                              {/* Modules in this category */}
                              <div className="space-y-1.5 pl-2 border-l-2 border-gray-100">
                                {group.modules.map((module) => (
                                  <div key={module.module_id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${module.completed ? 'border-green-200 bg-green-50/50' : 'border-gray-100 bg-gray-50/50'}`}>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${module.completed ? 'bg-green-100' : 'bg-gray-200'}`}>
                                      {module.completed ? (
                                        <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                      ) : (
                                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3"/></svg>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-800 truncate">{module.module_title}</p>
                                      {module.completed && module.completed_at && (
                                        <p className="text-xs text-green-600">{new Date(module.completed_at).toLocaleDateString('de-DE')}</p>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0">{module.content_type?.toUpperCase()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* End of Mitarbeiter tab */}
          </>
        )}

        {/* ===== TAB: Abschlussgespräche ===== */}
        {teamTab === 'exams' && (
          <>
        {/* Abschlussgespräche Section */}
        {(
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            {/* Google Calendar Widget */}
            {isVertriebsleiter && null}
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Abschlussgespräche</h2>

            {/* ===== TEAMLEITER: Two-Column Read-only Layout ===== */}
            {!isVertriebsleiter && (() => {
              const members = teamExamOverview || [];

              // Priority sort: ready_no_appt > exam_booked > exam_failed > exam_passed > not_ready
              const priorityOrder = { ready_for_exam: 0, exam_booked: 1, exam_failed: 2, exam_passed: 3 };
              const sorted = [...members].sort((a, b) => {
                const pa = priorityOrder[a.exam_status] ?? 4;
                const pb = priorityOrder[b.exam_status] ?? 4;
                return pa - pb;
              });

              const statusCfg = (s) => ({
                exam_passed:   { label: 'Bestanden',          bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300',  dot: 'bg-green-500',  icon: '✓' },
                exam_booked:   { label: 'Termin gebucht',     bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   dot: 'bg-blue-500',   icon: '📅' },
                ready_for_exam:{ label: 'Bereit für Gespräch',bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', dot: 'bg-indigo-500', icon: '!' },
                exam_failed:   { label: 'Nicht bestanden',    bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300',    dot: 'bg-red-500',    icon: '✗' },
              })[s] || { label: 'Noch nicht bereit', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-300', icon: '–' };

              const sel = selectedExamMember
                ? members.find(m => m.id === selectedExamMember)
                : null;

              if (members.length === 0) return (
                <div className="text-center py-12 text-gray-400 text-sm">Keine Vertriebspartner in deinem Team gefunden.</div>
              );

              return (
                <div className="flex gap-5 min-h-96">
                  {/* ── LEFT: Priority Member List ── */}
                  <div className="w-2/5 flex-shrink-0 space-y-2 overflow-y-auto pr-1">
                    {/* Legend */}
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-3">Sortiert nach Priorität</p>
                    {sorted.map(m => {
                      const cfg = statusCfg(m.exam_status);
                      const isSelected = selectedExamMember === m.id;
                      const apptDate = m.appointment?.date
                        ? new Date(m.appointment.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : null;
                      return (
                        <button key={m.id}
                          onClick={() => setSelectedExamMember(isSelected ? null : m.id)}
                          className={`w-full text-left rounded-xl px-4 py-3 border transition-all ${
                            isSelected
                              ? `${cfg.border} ${cfg.bg} ring-2 ring-offset-1 ring-blue-400 shadow-md`
                              : `border-gray-100 bg-white hover:border-gray-300 hover:shadow-sm`
                          }`}
                          data-testid={`exam-member-${m.id}`}>
                          <div className="flex items-center gap-3">
                            {/* Avatar + dot */}
                            <div className="relative flex-shrink-0">
                              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600">
                                {m.name.charAt(0)}
                              </div>
                              <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${cfg.dot}`} />
                            </div>
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{m.name}</p>
                              <p className="text-xs text-gray-400 truncate">{m.email}</p>
                            </div>
                            {/* Status badge */}
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.bg} ${cfg.text}`}>
                              {cfg.label}
                            </span>
                          </div>
                          {/* Appointment sub-row */}
                          {apptDate && (
                            <div className="mt-2 flex items-center gap-1.5 ml-12">
                              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                              </svg>
                              <span className="text-xs text-gray-500">{apptDate}</span>
                              {(m.cancellation_count > 0 || m.reschedule_count > 0) && (
                                <span className="text-xs text-orange-500 ml-1">· {(m.reschedule_count || m.cancellation_count)}× verschoben</span>
                              )}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* ── Divider ── */}
                  <div className="w-px bg-gray-100 flex-shrink-0" />

                  {/* ── RIGHT: Detail Panel ── */}
                  <div className="flex-1 min-w-0">
                    {!sel ? (
                      <div className="flex flex-col items-center justify-center h-full text-center py-16 text-gray-400">
                        <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                        </svg>
                        <p className="text-sm font-medium">Mitglied auswählen</p>
                        <p className="text-xs mt-1">Klicke links auf ein Teammitglied</p>
                      </div>
                    ) : (() => {
                      const cfg = statusCfg(sel.exam_status);
                      const apptDateFull = sel.appointment?.date
                        ? new Date(sel.appointment.date).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : null;
                      const isFuture = sel.appointment?.date && new Date(sel.appointment.date) > new Date();
                      const progress = sel.completion_rate || 0;

                      return (
                        <div className="space-y-4">
                          {/* Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-600">
                                {sel.name.charAt(0)}
                              </div>
                              <div>
                                <h3 className="text-base font-bold text-gray-900">{sel.name}</h3>
                                <p className="text-xs text-gray-400">{sel.email}</p>
                              </div>
                            </div>
                            <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                          </div>

                          {/* Status Cards */}
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                              <p className="text-xl font-bold text-gray-800">{progress.toFixed(0)}%</p>
                              <p className="text-xs text-gray-500 mt-0.5">Fortschritt</p>
                              {sel.total_modules > 0 && <p className="text-xs text-gray-400 mt-0.5">{sel.completed_modules}/{sel.total_modules} Module</p>}
                            </div>
                            <div className={`rounded-xl p-3 text-center border ${sel.failure_count > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                              <p className={`text-xl font-bold ${sel.failure_count > 0 ? 'text-red-600' : 'text-gray-800'}`}>{sel.failure_count}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Nicht bestanden</p>
                            </div>
                            <div className={`rounded-xl p-3 text-center border ${sel.cancellation_count > 0 ? 'bg-orange-50 border-orange-100' : 'bg-gray-50 border-gray-100'}`}>
                              <p className={`text-xl font-bold ${sel.cancellation_count > 0 ? 'text-orange-600' : 'text-gray-800'}`}>{sel.cancellation_count + (sel.reschedule_count || 0)}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Verschoben</p>
                            </div>
                          </div>

                          {/* Progress bar */}
                          {sel.total_modules > 0 && (
                            <div>
                              <div className="flex justify-between text-xs text-gray-400 mb-1">
                                <span>Schulungsfortschritt</span><span>{progress.toFixed(0)}%</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div className="h-2 rounded-full transition-all duration-700"
                                  style={{ width: `${Math.min(progress, 100)}%`, background: progress >= 70 ? '#22c55e' : progress >= 30 ? '#f59e0b' : '#ef4444' }} />
                              </div>
                            </div>
                          )}

                          {/* Appointment Detail */}
                          <div className={`rounded-xl border p-4 ${sel.exam_status === 'exam_booked' ? 'bg-blue-50 border-blue-200' : sel.exam_status === 'ready_for_exam' ? 'bg-indigo-50 border-indigo-200' : sel.exam_status === 'exam_passed' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                            <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">Abschlussgespräch</h4>
                            {sel.exam_status === 'ready_for_exam' && (
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                <div>
                                  <p className="text-sm font-semibold text-indigo-800">Wartet auf Terminbuchung</p>
                                  <p className="text-xs text-indigo-600 mt-0.5">Der Vertriebsleiter muss noch einen Termin eintragen.</p>
                                </div>
                              </div>
                            )}
                            {sel.exam_status === 'exam_booked' && apptDateFull && (
                              <div className="space-y-2.5">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                  <div>
                                    <p className="text-xs text-gray-500">Datum & Uhrzeit</p>
                                    <p className="text-sm font-semibold text-gray-800">{apptDateFull} {isFuture ? <span className="text-xs text-green-600 font-normal ml-1">· bevorstehend</span> : <span className="text-xs text-gray-400 font-normal ml-1">· vergangen</span>}</p>
                                  </div>
                                </div>
                                {sel.appointment?.vl_name && (
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                                    <div>
                                      <p className="text-xs text-gray-500">Zuständiger VL</p>
                                      <p className="text-sm font-semibold text-gray-800">{sel.appointment.vl_name}</p>
                                    </div>
                                  </div>
                                )}
                                {sel.appointment?.zoom_url && (
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                                    <div>
                                      <p className="text-xs text-gray-500">Zoom Meeting</p>
                                      <span className="text-xs text-blue-600 font-medium break-all">{sel.appointment.zoom_url}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            {sel.exam_status === 'exam_passed' && (
                              <div className="flex items-center gap-2">
                                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                <p className="text-sm font-semibold text-green-800">Abschlussgespräch erfolgreich bestanden</p>
                              </div>
                            )}
                            {sel.exam_status === 'exam_failed' && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                  <p className="text-sm font-semibold text-red-800">Nicht bestanden – Wiederholung erforderlich</p>
                                </div>
                                {sel.retry_categories?.length > 0 && (
                                  <p className="text-xs text-gray-500">Wiederholungskategorien: {sel.retry_categories.join(', ')}</p>
                                )}
                              </div>
                            )}
                            {!sel.exam_status && (
                              <p className="text-sm text-gray-500">Noch keine Prüfungsreife erreicht. Schulungen werden noch absolviert.</p>
                            )}
                          </div>

                          {/* Last Activity */}
                          {sel.last_activity && (
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                              <span>Letzte Aktivität: {new Date(sel.last_activity).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })()}

            {/* ===== VERTRIEBSLEITER: Full management view ===== */}
            {isVertriebsleiter && (
            <div className="space-y-6">
              {/* Ready for Exam */}
              <div>
                <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
                  <span className="mr-2">🎓</span>
                  Bereit für Abschlussgespräch ({examCandidates.ready_for_exam?.length || 0})
                </h3>
                {examCandidates.ready_for_exam && examCandidates.ready_for_exam.length > 0 ? (
                  <div className="space-y-3">
                    {examCandidates.ready_for_exam.map((candidate) => (
                      <div key={candidate.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                            <p className="text-sm text-gray-600">{candidate.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Wartet auf Terminbuchung
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Keine Kandidaten bereit für Gespräch</p>
                )}
              </div>

              {/* Exam Booked */}
              <div>
                <h3 className="text-lg font-medium text-green-900 mb-4 flex items-center">
                  <span className="mr-2">📅</span>
                  Gespräch gebucht ({examCandidates.exam_booked?.length || 0})
                </h3>
                {examCandidates.exam_booked && examCandidates.exam_booked.length > 0 ? (
                  <div className="space-y-3">
                    {examCandidates.exam_booked.map((candidate) => (
                      <div key={candidate.id} className="border border-green-200 bg-green-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                            <p className="text-sm text-gray-600">{candidate.email}</p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setSelectedRetryCategories(null);
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                              data-testid={`btn-passed-booked-${candidate.id}`}
                            >
                              Bestanden
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setSelectedRetryCategories([]);
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                              data-testid={`btn-failed-booked-${candidate.id}`}
                            >
                              Nicht bestanden
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Keine gebuchten Gespräche</p>
                )}
              </div>

              {/* Failed - Need Retry */}
              <div>
                <h3 className="text-lg font-medium text-orange-900 mb-4 flex items-center">
                  <span className="mr-2">📚</span>
                  Module wiederholen ({examCandidates.exam_failed?.length || 0})
                </h3>
                {examCandidates.exam_failed && examCandidates.exam_failed.length > 0 ? (
                  <div className="space-y-3">
                    {examCandidates.exam_failed.map((candidate) => (
                      <div key={candidate.id} className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                            <p className="text-sm text-gray-600">{candidate.email}</p>
                            {candidate.retry_categories && candidate.retry_categories.length > 0 && (
                              <p className="text-xs text-orange-700 mt-1">
                                {candidate.retry_categories.length} Kategorie(n) zur Wiederholung
                              </p>
                            )}
                          </div>
                          <span className="text-sm text-orange-700 font-medium">In Bearbeitung</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Keine Wiederholungen erforderlich</p>
                )}
              </div>
            </div>
            )}
          </div>
        )}

        {/* Category Selection Modal for "Nicht bestanden" */}
        {selectedCandidate && selectedRetryCategories !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Kategorien zur Wiederholung wählen
                </h2>
                <button
                  onClick={() => {
                    setSelectedCandidate(null);
                    setSelectedRetryCategories([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-gray-600 mb-6">
                Wählen Sie die Kategorien aus, die <strong>{selectedCandidate.name}</strong> wiederholen soll:
              </p>

              <div className="space-y-3 mb-6">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedRetryCategories.includes(category.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleCategoryForRetry(category.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedRetryCategories.includes(category.id)}
                          onChange={() => toggleCategoryForRetry(category.id)}
                          className="h-5 w-5 text-blue-600"
                        />
                        <div>
                          <h4 className="font-medium text-gray-900">{category.title}</h4>
                          <p className="text-sm text-gray-600">{category.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedCandidate(null);
                    setSelectedRetryCategories([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handleExamResult(selectedCandidate.id, false)}
                  disabled={selectedRetryCategories.length === 0}
                  className={`px-4 py-2 rounded-lg text-white font-medium transition-colors ${
                    selectedRetryCategories.length === 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Nicht bestanden markieren
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal for "Bestanden" */}
        {selectedCandidate && selectedRetryCategories === null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Abschlussgespräch bestanden
                </h2>
                <button
                  onClick={() => {
                    setSelectedCandidate(null);
                    setSelectedRetryCategories([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <div className="text-center mb-4">
                  <div className="text-5xl mb-3">🎉</div>
                  <p className="text-gray-700">
                    Möchten Sie <strong>{selectedCandidate.name}</strong> als bestanden markieren?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Das Zertifikat wird automatisch generiert und per E-Mail versendet.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedCandidate(null);
                    setSelectedRetryCategories([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handleExamResult(selectedCandidate.id, true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Ja, bestanden markieren
                </button>
              </div>
            </div>
          </div>
        )}

        {/* End of Abschlussgespräche tab */}
          </>
        )}

        {/* Module Creation Section - only for Teamleiter */}
        {teamTab === 'members' && !isVertriebsleiter && canCreateModules && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Team-Module verwalten</h2>
              <button
                onClick={() => setShowCreateModule(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Neues Modul erstellen
              </button>
            </div>
            
            {/* Team Modules List */}
            <div className="space-y-3">
              {teamModules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Noch keine Team-Module erstellt.</p>
                </div>
              ) : (
                teamModules.map((module) => (
                  <div key={module.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{module.title}</h4>
                        <p className="text-sm text-gray-600">{module.description}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span className="bg-gray-100 px-2 py-1 rounded">
                            {module.content_type.toUpperCase()}
                          </span>
                          {module.duration_minutes && (
                            <span>{module.duration_minutes} Min</span>
                          )}
                          <span className={`px-2 py-1 rounded ${
                            module.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {module.is_approved ? 'Genehmigt' : 'Ausstehend'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {module.target_users?.length || 0} Zielbenutzer
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Module Editor */}
      {showCreateModule && (
        <EnhancedModuleEditor
          isOpen={showCreateModule}
          onClose={() => setShowCreateModule(false)}
          onSuccess={handleModuleSuccess}
        />
      )}
    </div>
  );
};

export default TeamDashboard;
